import { unaryRpc } from "@redotech/rpc/definition";
import * as z from "zod/v4";

/**
 * Which set of disputes to count for per-merchant Won/Lost/Total. Historically
 * this dashboard's Merchants tab counted "all" disputes while the win-rate
 * header counted only AI-touched ones, producing reconcilable-but-confusing
 * numbers. Exposing the choice as a dashboard control makes it explicit.
 */
export const reclaimDenominator = z.enum(["all", "ai-touched"]);
export type ReclaimDenominator = z.infer<typeof reclaimDenominator>;

export const reclaimMerchantDealSizeByCurrencySchema = z.object({
  currency: z.string(),
  /**
   * Trailing-365d chargeback volume in this currency, used as the volume
   * input to the deal-size formula.
   */
  trailingYearChargebackVolume: z.number(),
  /**
   * Annualized expected fees in this currency:
   * trailingYearChargebackVolume × globalWinRate × feePercentage.
   */
  dealSize: z.number(),
});

export const reclaimMerchantRowSchema = z.object({
  teamId: z.string(),
  teamName: z.string(),
  accountId: z.string(),
  totalDisputes: z.number().int(),
  openChargebacks: z.number().int(),
  evidenceGenerated: z.number().int(),
  won: z.number().int(),
  lost: z.number().int(),
  automateEvidenceEnabled: z.boolean(),
  billingEnabled: z.boolean(),
  reclaimShopifyAppInstalledAt: z.date().nullable(),
  evidenceGeneratedAmount: z.number(),
  /**
   * Per-currency deal-size breakdown for this merchant. Each entry uses
   * this merchant's win rate (with global fallback when zero finalized
   * disputes in the window) and this merchant's fee rate. Empty when the
   * merchant has no chargeback volume in the trailing year.
   */
  dealSizeByCurrency: z.array(reclaimMerchantDealSizeByCurrencySchema),
  /** Decimal fee rate used for this merchant's deal size (0.25 = 25%). */
  feeRate: z.number(),
  /** Decimal win rate used for this merchant's deal size. */
  winRateUsed: z.number(),
  /** True when winRateUsed is the global fallback (zero finalized AI-touched
   * disputes for this merchant in the 30d window). */
  winRateIsGlobal: z.boolean(),
});

export interface ReclaimMerchantRow
  extends z.infer<typeof reclaimMerchantRowSchema> {}

export const getReclaimMerchants = unaryRpc({
  name: "getReclaimMerchants" as const,
  input: z.object({ denominator: reclaimDenominator.optional() }),
  output: z.object({
    merchants: z.array(reclaimMerchantRowSchema),
    skippedCount: z.number().int(),
    denominator: reclaimDenominator,
    /**
     * Global win rate (last 30d AI-touched, excl. Shopify-protected) used
     * as the multiplier for every per-merchant dealSize. Exposed so the UI
     * can show how the column was computed.
     */
    globalWinRate: z.number(),
  }),
});
